import {
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Input,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  Layers,
  RotateCcw,
  Save,
  Settings,
} from 'react-feather';
import { useForm, useWatch } from 'react-hook-form';
import { Link, Redirect } from 'react-router-dom';
import { Card } from '../../components/Card';
import { PageContainer } from '../../components/container/PageContainer';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useCurrentUserData } from '../../hooks/useCurrentUserData';
import { expired } from '../../utils/expired';
import { LoadingPage } from '../util-pages/LoadingPage';
import { DeleteUserAlert } from './DeleteUserAlert';
import { ProfileCard } from './ProfileCard';

interface ProfilePageProps {}

type FormState = { email: string; displayName: string };

const status = new Map([
  [
    'trial',
    {
      title: 'Testversion',
      icon: (
        <Icon
          my={2}
          width="3em"
          height="3em"
          as={AlertTriangle}
          color="orange"
        />
      ),
    },
  ],
  [
    'active',
    {
      title: 'aktiv',
      icon: <Icon mt={2} width="3em" height="3em" as={Check} color="green" />,
    },
  ],
  [
    'inactive',
    {
      title: 'inaktiv',
      icon: (
        <Icon my={2} width="3em" height="3em" as={AlertCircle} color="red" />
      ),
    },
  ],
]);

export const ProfilePage: React.FC<ProfilePageProps> = () => {
  const currentUser = useCurrentUser();
  const currentUserData = useCurrentUserData();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, formState, handleSubmit, reset, control } =
    useForm<FormState>();
  const { errors } = formState;

  const resetForm = () =>
    reset({
      email: currentUser?.email || '',
      displayName: currentUser?.displayName || '',
    });

  const displayNameWatcher = useWatch({
    control,
    name: 'displayName',
    defaultValue: '',
  });
  const hasFormDiff = displayNameWatcher !== currentUser?.displayName;

  useEffect(() => {
    resetForm();
  }, [currentUser, reset]);

  if (!currentUser) return <Redirect to="/login/profile" />;

  if (!currentUserData) return <LoadingPage isHeader />;

  const onSubmit = handleSubmit(async ({ displayName }) => {
    setIsSubmitting(true);
    try {
      await currentUser.updateProfile({ displayName });
      setIsSubmitting(false);
    } catch (e) {
      setIsSubmitting(false);
    }
  });

  return (
    <PageContainer isHeader>
      <SimpleGrid columns={[1, 1, 3]} gap={[3, 3, 4]} mb={[6, 6, 8]}>
        <ProfileCard
          index={0}
          icon={status.get(currentUserData.status)?.icon}
          label={status.get(currentUserData.status)?.title}
          subLabel={
            (currentUserData.status === 'trial' &&
              !expired(currentUserData) &&
              `Läuft am ${moment(currentUserData.trialExpiration).format(
                'LL'
              )} aus.`) ||
            (currentUserData.status === 'trial' &&
              expired(currentUserData) &&
              `ist am ${moment(currentUserData.trialExpiration).format(
                'LL'
              )} ausgelaufen.`) ||
            undefined
          }
          ctaHref="https://google.de"
          ctaLabel={
            (currentUserData.status !== 'active' && 'Jetzt verlängern') ||
            undefined
          }
        />
        <ProfileCard
          index={0}
          icon={
            <Text fontSize="4em" lineHeight="1em">
              {currentUserData.courseMinutes}
            </Text>
          }
          label="Minuten"
          subLabel="in Online-Kursen absolviert"
        />
        <ProfileCard
          index={0}
          icon={
            <Text fontSize="4em" lineHeight="1em">
              {currentUserData.courseCount || 0}
            </Text>
          }
          label={`Kurs${+currentUserData.courseCount !== 1 ? 'e' : ''} besucht`}
          subLabel={
            (currentUserData.courseCount === 0 && 'Aller Anfang ist schwer') ||
            (currentUserData.courseCount < 10 && 'Weiter so!') ||
            'Starkes Stück!'
          }
        />
      </SimpleGrid>

      <Card
        icon={Settings}
        title="Einstellungen"
        animate={true}
        animationDelay={0.1}
      >
        <form onSubmit={onSubmit}>
          <SimpleGrid gap={4} columns={[1, 1, 2]} mb={4}>
            <FormControl mt={3} isInvalid={!!errors.displayName}>
              <FormLabel>Name</FormLabel>
              <Input
                focusBorderColor="brand.500"
                defaultValue=""
                type="text"
                {...register('displayName')}
              />
            </FormControl>
            <FormControl mt={3} isInvalid={!!errors.email} isDisabled={true}>
              <FormLabel>E-Mail (kann nicht geändert werden)</FormLabel>
              <Input
                focusBorderColor="brand.500"
                defaultValue=""
                type="text"
                {...register('email')}
              />
            </FormControl>
          </SimpleGrid>
          <Flex w="full" justify="flex-end">
            {hasFormDiff && (
              <IconButton
                aria-label="Abbrechen"
                focusBorderColor="brand.500"
                icon={<Icon as={RotateCcw} />}
                variant="outline"
                onClick={resetForm}
                mr={3}
              />
            )}
            <Button
              isDisabled={!hasFormDiff}
              focusBorderColor="brand.500"
              isLoading={isSubmitting}
              leftIcon={<Icon as={Save} />}
              onClick={onSubmit}
              type="submit"
              colorScheme={hasFormDiff ? 'brand' : 'gray'}
            >
              Speichern
            </Button>
          </Flex>
        </form>
        <Divider my={4} />
        <DeleteUserAlert currentUser={currentUser} />
        {currentUserData.isAdmin && (
          <>
            <Divider my={4} />
            <Button leftIcon={<Icon as={Layers} />} as={Link} to="/admin">
              Admin-Portal
            </Button>
          </>
        )}
      </Card>
    </PageContainer>
  );
};
